<script setup lang="ts">
import IconPerson from '~icons/material-symbols/person-outline-rounded'
import IconTune from '~icons/material-symbols/tune-rounded'
import IconBadge from '~icons/material-symbols/badge-outline-rounded'
import IconMail from '~icons/material-symbols/alternate-email-rounded'
import IconLogout from '~icons/material-symbols/logout-rounded'
import { computed, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useKeycloak } from '@/shared/composables/useKeycloak'
import { useStoreUser } from '@/stores/user'

import Icon from '@/shared/components/Icon.vue'
import { Switch } from '@/components/ui/switch'

import type { Component } from 'vue'

const { logout, userProfile } = useKeycloak()
const storeUser = useStoreUser()
const { stateSettings } = storeToRefs(storeUser)

type TabId = 'profile' | 'preferences'
const tabs: { id: TabId, label: string, icon: Component }[] = [
  { id: 'profile', label: 'Profile', icon: IconPerson },
  { id: 'preferences', label: 'Preferences', icon: IconTune },
]
const activeTab = ref<TabId>('profile')

const fullName = computed(() => {
  if (!userProfile.value) return ''
  return [userProfile.value.given_name, userProfile.value.family_name].filter(Boolean).join(' ')
})

const displayName = computed(() => fullName.value || userProfile.value?.preferred_username || 'My profile')

const initials = computed(() => {
  const source = fullName.value || userProfile.value?.preferred_username || ''
  const parts = source.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
})

const profileFields = computed(() => [
  { label: 'Name', value: fullName.value, icon: IconPerson },
  { label: 'Username', value: userProfile.value?.preferred_username, icon: IconBadge },
  { label: 'Email', value: userProfile.value?.email, icon: IconMail },
])

const breadcrumbIcons = computed({
  get: () => stateSettings.value.breadcrumb_icons,
  set: (value: boolean) => storeUser.setBreadcrumbIcons({ breadcrumbIcons: value }),
})

const doLogout = async (): Promise<void> => {
  await logout()
  // Keycloak handles redirect after logout
}

definePage({
  meta: {
    label: 'Profile',
    icon: IconPerson,
  },
})
</script>

<template>
  <div class="flex w-full flex-col">
    <!-- Header -->
    <div class="mx-auto flex w-full max-w-5xl items-center gap-4 px-6 py-5">
      <div
        class="bg-primary text-primary-foreground flex h-14 w-14 shrink-0 items-center justify-center rounded-full text-lg font-semibold"
      >
        {{ initials }}
      </div>
      <div class="min-w-0">
        <h1 class="text-foreground truncate text-xl font-semibold">{{ displayName }}</h1>
        <p
          v-if="userProfile?.email"
          class="text-muted-foreground truncate text-sm"
        >
          {{ userProfile.email }}
        </p>
      </div>
    </div>

    <!-- Tabs: full-screen-width bottom divider, capped buttons -->
    <div class="border-border border-b">
      <div class="mx-auto flex w-full max-w-5xl gap-1 pr-6">
        <button
          v-for="tab in tabs"
          :key="tab.id"
          type="button"
          class="-mb-px flex cursor-pointer items-center gap-2 border-b-2 px-3 py-2 text-sm font-medium transition-colors"
          :class="
            activeTab === tab.id
              ? 'border-primary text-foreground'
              : 'text-muted-foreground hover:text-foreground border-transparent'
          "
          @click="activeTab = tab.id"
        >
          <Icon
            :icon="tab.icon"
            class="h-4 w-4 shrink-0"
          />
          {{ tab.label }}
        </button>
      </div>
    </div>

    <!-- Content (capped) -->
    <div class="mx-auto w-full max-w-5xl">
      <!-- Profile tab -->
      <template v-if="activeTab === 'profile'">
      <div
        class="text-muted-foreground border-border mt-6 border-b px-6 py-2 text-xs font-semibold tracking-wide uppercase"
      >
        Account
      </div>
      <div
        v-for="field in profileFields"
        :key="field.label"
        class="border-border flex items-center gap-3 border-b px-6 py-3"
      >
        <Icon
          :icon="field.icon"
          class="h-4 w-4 shrink-0"
        />
        <span class="text-muted-foreground w-28 shrink-0 text-sm">{{ field.label }}</span>
        <span class="text-foreground min-w-0 flex-1 truncate text-sm">{{ field.value || '—' }}</span>
      </div>

      <div
        class="text-muted-foreground border-border mt-6 border-b px-6 py-2 text-xs font-semibold tracking-wide uppercase"
      >
        Session
      </div>
      <button
        type="button"
        class="hover:bg-accent/40 border-border flex w-full cursor-pointer items-center gap-3 border-b px-6 py-3 text-left transition-colors"
        @click="doLogout"
      >
        <Icon
          :icon="IconLogout"
          class="h-4 w-4 shrink-0"
        />
        <span class="text-foreground text-sm font-medium">Log out</span>
      </button>
    </template>

    <!-- Preferences tab -->
    <template v-else-if="activeTab === 'preferences'">
      <div
        class="text-muted-foreground border-border mt-6 border-b px-6 py-2 text-xs font-semibold tracking-wide uppercase"
      >
        Breadcrumb
      </div>
      <div class="border-border flex items-center justify-between gap-4 border-b px-6 py-3">
        <div class="min-w-0">
          <p class="text-foreground text-sm font-medium">Breadcrumb icons</p>
          <p class="text-muted-foreground text-sm">
            Show the leading folder, project, and concept icons in the breadcrumb.
          </p>
        </div>
        <Switch v-model="breadcrumbIcons" />
      </div>
    </template>
    </div>
  </div>
</template>
